import type { FrenchDict } from './fr'

// Same shape as fr — strict typing forces translators to keep keys in sync.
export const en: FrenchDict = {
  common: {
    yes: 'Yes',
    no: 'No',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    open: 'Open',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    loading: 'Loading…',
    saving: 'Saving…',
    sending: 'Sending…',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    add: 'Add',
    remove: 'Remove',
    yes_remove: 'Yes, remove',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    welcome: 'Welcome',
    logout: 'Log out',
    settings: 'Settings',
    language: 'Language',
    fcfa_short: 'FCFA',
    optional: 'optional',
    required: 'required',
    submit: 'Submit',
    create: 'Create',
    update: 'Update',
    refresh: 'Refresh',
    retry: 'Retry',
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    date: 'Date',
    time: 'Time',
    amount: 'Amount',
    quantity: 'Quantity',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    phone: 'Phone',
    address: 'Address',
    notes: 'Notes',
    description: 'Description',
    status: 'Status',
    actions: 'Actions',
    details: 'Details',
    history: 'History',
    not_authenticated: 'Not authenticated',
    forbidden: 'Access denied',
    not_found: 'Not found',
    network_error: 'Network error',
    please_try_again: 'Please try again',
    created: 'Created',
    updated: 'Updated',
    deleted: 'Deleted',
    validated: 'Validated',
    cancelled: 'Cancelled',
    sent: 'Sent',
    received: 'Received',
    refunded: 'Refunded',
    coming_soon: 'Coming soon',
    no_results: 'No results',
    empty_list: 'Empty list',
    or: 'or',
    and: 'and',
    of: 'of',
    on: 'on',
    by: 'by',
    from: 'from',
    to: 'to',
    at: 'at',
  },
  nav: {
    dashboard: 'Dashboard',
    assistant_ai: '🤖 AI Assistant',
    inbox: 'Inbox',
    stocks: 'Stocks',
    inventory: 'Inventory',
    batches: 'Batches',
    cashier: 'Cashier',
    cash_closure: 'Cash closure',
    customer_credits: 'Customer credits',
    deliveries: 'Deliveries',
    insurance_claims: 'Insurance claims',
    prescriptions: 'Prescriptions',
    proforma: 'Quotes',
    payments: 'Payments',
    accounting: 'Accounting',
    tva: 'VAT',
    notifications: 'Notifications',
    loyalty: 'Loyalty',
    rewards: 'Rewards',
    analytics: 'Analytics',
    anomalies: '⚠️ Anomalies',
    ratings: '⭐ Ratings',
    hr: 'HR',
    sms_campaigns: '📱 SMS Campaigns',
    cashiers: 'Cashiers',
    suppliers: 'Suppliers',
    purchase_orders: 'Purchase orders',
    auto_replenish: 'Auto-replenish',
    backups: 'Backups',
    audit: 'Audit',
    formation: 'Training',
    settings: 'Settings',
    home: 'Home',
    carnet: 'Health book',
    find: 'Find',
    orders: 'Orders',
    points: 'Points',
    profile: 'Profile',
    navigation: 'Navigation',
    earnings: 'Earnings',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Access your PharmaConnect dashboard',
    email_label: 'Email',
    email_placeholder: 'your@email.cm',
    password_label: 'Password',
    password_placeholder: '••••••••',
    submit: 'Sign in',
    forgot_password: 'Forgot password?',
    invalid_credentials: 'Invalid email or password',
    or_pin: 'Cashier? PIN login',
    pharmacist_tab: 'Pharmacist',
    cashier_tab: 'Cashier',
    signup_tab: 'Sign up',
    full_name: 'Full name',
    referral_code: 'Referral code (optional)',
    referral_hint: '🎁 You will earn 50 points on your first purchase (and so will your sponsor).',
    create_account: 'Create my account',
    space_patient: 'Patient space',
    space_driver: 'Driver space',
    space_driver_hint:
      'Account created by your PharmaConnect administrator after CNI validation.',
    driver_become: 'No driver account yet? Become a driver',
    switch_patient: 'Are you a patient? Sign in here',
    switch_driver: 'Are you a driver? Sign in here',
    validation_failed: 'Valid email and password ≥ 8 characters required',
  },
  forgot: {
    title: 'Forgot password',
    subtitle: 'Enter your email to receive a reset link.',
    send_link: 'Send link',
    sent_title: 'Email sent',
    sent_message:
      'If an account exists for {email}, a reset link has just been sent. Check your inbox (and spam folder).',
    sent_hint:
      'The link expires in 1 hour. Open it in any browser, set your new password, then come back here to sign in.',
    back_to_login: '← Back to sign in',
  },
  reset: {
    title: 'New password',
    subtitle: 'Choose a new secure password.',
    new_password: 'New password',
    confirm_password: 'Confirm password',
    submit: 'Update',
    checking: 'Verifying reset link…',
    link_invalid:
      'If you see this message for more than a few seconds, the link is invalid or has expired (max 1h after sending).',
    request_new_link: 'Request a new link',
    too_short: 'Password too short (8 characters minimum)',
    mismatch: 'Passwords do not match',
    success: 'Password updated',
  },
  dashboard: {
    title: 'Dashboard',
    today_revenue: "Today's revenue",
    today_sales: "Today's sales",
    low_stock: 'Low stock',
    imminent_stockouts: 'Stockouts within 7d',
    deliveries_active: 'Active deliveries',
    overdue_credits: 'Overdue credits',
    pending_accounting: 'Entries to validate',
    insurance_pending: 'Claims to process',
    customize_button: '⚙ Customize',
    customize_title: 'Customize dashboard',
    customize_description:
      'Choose which cards to display. Your preferences sync across all your devices.',
    customize_tip:
      'Tip: hiding a card does not delete the data — re-enable it whenever you want.',
    no_pharmacy: 'No pharmacy associated with your account.',
  },
  user_role: {
    owner: 'Owner',
    manager: 'Manager',
    cashier: 'Cashier',
    super_admin: 'Super admin',
    patient: 'Patient',
    driver: 'Driver',
    doctor: 'Doctor',
  },
  home: {
    hello: 'Hello',
    qr_urgence: 'Emergency QR',
    notifications: 'Notifications',
    pharmacy_nearest: 'Nearest pharmacy',
    on_call_pharmacies: 'On-call pharmacies',
    quick_actions: 'Quick actions',
    reminders_today: "Today's reminders",
    no_reminders: 'No reminders for today — well done!',
    recent_vitals: 'Recent vital signs',
    loyalty_balance: 'Loyalty points',
    points: 'pts',
    your_orders: 'Your orders',
    no_orders: 'No active orders.',
    take_pill: 'Take',
    pill_taken: 'Taken',
  },
  carnet: {
    title: 'Health book',
    vitals: 'Vital signs',
    add_vital: 'Add a vital sign',
    reminders: 'Medication reminders',
    add_reminder: 'Add a reminder',
    no_vitals: 'No vital signs recorded.',
    no_reminders: 'No active reminders.',
    blood_pressure: 'Blood pressure',
    glucose: 'Glucose',
    weight: 'Weight',
    temperature: 'Temperature',
    measured_at: 'Measured on',
    medication_name: 'Medication name',
    dosage: 'Dosage',
    times: 'Times',
    frequency: 'Frequency',
    daily: 'Daily',
    twice_daily: 'Twice daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
  },
  profile: {
    title: 'Profile',
    edit: 'Edit my profile',
    blood_group: 'Blood group',
    weight_kg: 'Weight (kg)',
    height_cm: 'Height (cm)',
    allergies: 'Allergies',
    chronic_diseases: 'Chronic diseases',
    emergency_contacts: 'Emergency contacts',
    qr_emergency: 'Emergency QR',
    qr_share_hint:
      'Show this QR to a healthcare worker in an emergency — they can access your vital info without internet.',
    saved: 'Changes saved',
    save_failed: 'Save failed',
    open_inbox: '📥 Notifications',
    open_documents: '📁 My documents',
    open_family: '👨‍👩‍👧 Family mode',
    open_consents: '🔐 Doctor consents',
    open_assurances: '🛡 My insurances',
    open_quiz: '🎓 Health training',
    open_appointments: '📅 My appointments',
    open_vaccinations: '💉 Vaccinations',
    open_pharmacovigilance: '⚠ Report side effect',
    open_grossesse: '🤰 Pregnancy tracker',
    open_regles: '🌸 Period tracker',
    open_depistage: '🎗 Cancer screening',
    open_sante_mentale: '🧠 Mental health',
    open_nutrition: '🥗 Nutrition plan',
    open_activite: '🏃 Physical activity',
    open_dons_sang: '🩸 Blood donations',
    open_carte_donneur: '🆔 Donor card',
    export_pdf: '📄 Export my record (PDF)',
  },
  cashier: {
    title: 'Cashier',
    cart: 'Cart',
    empty_cart: 'Cart is empty.',
    add_medication: 'Add medication',
    customer: 'Customer',
    anonymous: 'Anonymous',
    select_patient: 'Select a patient',
    total: 'Total',
    subtotal: 'Subtotal',
    discount: 'Discount',
    insurance: 'Insurance',
    coverage: 'Coverage',
    payment_method: 'Payment method',
    cash: 'Cash',
    mtn_momo: 'MTN MoMo',
    orange_money: 'Orange Money',
    camerpay: 'CamerPay',
    card: 'Card',
    pay: 'Charge',
    receipt: 'Receipt',
    print_receipt: 'Print receipt',
    send_receipt: 'Send receipt',
    sale_complete: 'Sale recorded',
    insufficient_stock: 'Insufficient stock',
  },
  medications: {
    title: 'Medications',
    add: 'Add a medication',
    name: 'Name',
    dci: 'INN',
    form: 'Form',
    dosage: 'Dosage',
    barcode: 'Barcode',
    price: 'Price',
    quantity: 'Quantity',
    alert_threshold: 'Alert threshold',
    expiry: 'Expiry',
    supplier: 'Supplier',
    category: 'Category',
    in_stock: 'In stock',
    out_of_stock: 'Out of stock',
    low_stock: 'Low stock',
    expires_soon: 'Expires soon',
    expired: 'Expired',
  },
  accounting: {
    title: 'Accounting',
    journal: 'Journal',
    new_entry: 'New entry',
    income: 'Income',
    expense: 'Expense',
    pending: 'Pending',
    validated: 'Validated',
    label: 'Label',
    category: 'Category',
    reference: 'Reference',
    total_income: 'Total income',
    total_expense: 'Total expenses',
    net_result: 'Net result',
    export_pdf: 'PDF export',
    export_csv: 'CSV export',
    validate: 'Validate',
    cant_modify_validated: 'Cannot modify a validated entry',
  },
  deliveries: {
    title: 'Deliveries',
    pending: 'Pending',
    paid: 'Paid',
    assigned: 'Assigned',
    in_transit: 'In transit',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    track_driver: 'Track driver',
    confirm_delivery: 'Confirm delivery',
    scan_driver_qr: 'Scan driver QR',
    driver_eta: 'ETA',
    delivery_address: 'Delivery address',
    delivery_fee: 'Delivery fee',
    rate_driver: 'Rate driver',
  },
  prescriptions: {
    title: 'Prescriptions',
    active: 'Active',
    dispensed: 'Dispensed',
    expired: 'Expired',
    prescribed_by: 'Prescribed by',
    prescribed_on: 'On',
    medications: 'Medications',
    duration: 'Duration',
    signed_digital: 'Digital signature',
    verify: 'Verify signature',
    upload_image: 'Prescription photo',
    request_quote: 'Request a quote',
  },
  loyalty: {
    title: 'Loyalty',
    balance: 'Balance',
    tier: 'Tier',
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
    platinum: 'Platinum',
    earned_total: 'Total earned',
    spent_total: 'Total spent',
    next_tier_at: 'Next tier at',
    history: 'History',
    earn_action: 'Earned',
    spend_action: 'Spent',
    bonus_action: 'Bonus',
    expire_action: 'Expired',
    rewards_catalog: 'Rewards catalog',
    redeem: 'Redeem',
    insufficient_points: 'Not enough points',
  },
  insurance: {
    title: 'My insurances',
    add: '+ Add insurance',
    provider: 'Provider',
    policy_number: 'Policy number',
    coverage_rate: 'Coverage rate (%)',
    is_primary: 'Primary insurance',
    no_insurance: 'No insurance declared. Tap + to add one.',
    declare_at_caisse:
      'Declare your health insurance so the pharmacy can apply third-party payment automatically at checkout.',
    not_in_list: "My insurance isn't in the list",
    custom_name: 'Insurance provider name',
    saved: 'Insurance saved',
    auto_applied: 'It will be applied automatically at checkout.',
  },
  family: {
    title: 'Family mode',
    why: 'Why?',
    why_text:
      "Add family members (children, elderly parents…) to manage their vital signs, medication reminders and vaccination record from your own account.",
    add_member: 'Add a member',
    members: 'Registered members',
    no_members: 'Nobody yet.',
    full_name: 'Full name',
    date_of_birth: 'Date of birth',
    blood_group: 'Blood group',
    bootstrap_vaccines: '💉 Generate vaccine reminders',
    open_growth: '📏 Growth & development',
    add_dob_first: 'Add a date of birth to enable EPI vaccine reminders.',
  },
  pregnancy: {
    title: '🤰 Pregnancy tracker',
    start_button: '🤰 Create my record',
    intro:
      'Digital pregnancy record with WHO calendar — 16 appointments automatically scheduled from your last menstrual period (LMP).',
    lmp_date: 'Last menstrual period (YYYY-MM-DD) *',
    gravida: 'Gravidity (# pregnancies)',
    parity: 'Parity (# deliveries)',
    partner_phone: 'Partner / emergency phone',
    upcoming: 'Upcoming visits',
    done: 'Completed visits',
    mark_done: '✓ Done',
    next_rdv: '🔔 Next visit',
    close: 'Close tracking',
    close_choose: 'What is the outcome?',
    close_delivered: 'Delivery',
    close_lost: 'Loss',
    close_archived: 'Pregnancy record archived.',
  },
  regles: {
    title: '🌸 Period tracker',
    intro:
      'Menstrual cycle record. Your data is 100% private — only visible to you.',
    started_today: '🩸 My period started today',
    ended_today: '✓ My period has ended',
    flow_intensity: 'Flow intensity',
    flow_light: 'Light',
    flow_medium: 'Medium',
    flow_heavy: 'Heavy',
    flow_very_heavy: 'Very heavy',
    next_period: 'Next period',
    ovulation: 'Estimated ovulation',
    fertile_window: 'Fertile window',
    statistics: '📊 Statistics',
    cycles_logged: 'cycles logged',
    avg_cycle: 'average cycle',
    avg_duration: 'period duration',
    regularity: 'regularity',
    history: '📅 History',
    no_cycles: 'No cycles recorded.',
    phase_menstruation: '🩸 Menstruation',
    phase_follicular: '🌱 Follicular',
    phase_ovulation: '💜 Ovulation',
    phase_luteal: '🌙 Luteal',
  },
  depistage: {
    title: '🎗 Cancer screening',
    intro:
      "Companion tool for preventive cancer screenings (mammography, pap smear, breast exam, HPV test). WHO recommendations.",
    dob_required: '📅 Date of birth required',
    dob_hint:
      'To compute your screening eligibility, the app needs your date of birth.',
    mammogram: 'Mammography',
    pap_smear: 'Pap smear (cervical)',
    breast_exam: 'Breast palpation',
    hpv_test: 'HPV test',
    last: 'Last',
    next: 'Next',
    log_done: '+ I had this screening',
    result_normal: 'Normal',
    result_abnormal: 'Abnormal',
    result_inconclusive: 'Inconclusive',
    result_pending: 'Pending',
    status_unknown_age: 'Enter your date of birth',
    status_too_young: 'Not yet eligible',
    status_past_age_range: 'No longer in age range',
    status_never_done: 'To plan',
    status_overdue: 'Overdue',
    status_due_soon: 'Due soon',
    status_on_time: 'Up to date',
    saved: 'Screening recorded.',
  },
  mental_health: {
    title: '🧠 Mental health',
    intro:
      'Validated self-assessment tools (PHQ-9, GAD-7) and mood journal. Your data is 100% private — only visible to you.',
    tab_summary: 'Summary',
    tab_assessment: 'Self-eval',
    tab_mood: 'Mood',
    phq9: 'PHQ-9 (depression)',
    gad7: 'GAD-7 (anxiety)',
    not_yet: 'Not yet',
    mood_label: 'Mood',
    energy_label: 'Energy',
    anxiety_label: 'Anxiety',
    save_mood: '+ Save',
    saved: 'Mood logged for today.',
    severity_minimal: 'Minimal',
    severity_mild: 'Mild',
    severity_moderate: 'Moderate',
    severity_moderately_severe: 'Moderately severe',
    severity_severe: 'Severe',
    red_flag_title: '⚠ Immediate help',
    red_flag_message:
      'Your answers suggest you need urgent help. Call 119 (Cameroon emergency) or go to the nearest ER. You are not alone.',
    call_119: 'Call 119',
    resources: '📚 Help resources',
  },
  nutrition: {
    title: '🥗 Nutrition plan',
    intro:
      'Personalized recommendations based on your BMI, goal and health profile.',
    bmi: 'BMI',
    bmi_underweight: 'Underweight',
    bmi_normal: 'Normal',
    bmi_overweight: 'Overweight',
    bmi_obese_1: 'Obesity I',
    bmi_obese_2: 'Obesity II',
    bmi_obese_3: 'Obesity III',
    goal_lose: 'Lose weight',
    goal_maintain: 'Maintain weight',
    goal_gain: 'Gain weight',
    goal_pregnancy: 'Support pregnancy',
    goal_breastfeed: 'Support breastfeeding',
    activity_sedentary: 'Sedentary',
    activity_light: 'Light (1-2x/wk)',
    activity_moderate: 'Moderate (3-4x/wk)',
    activity_active: 'Active (5x/wk)',
    activity_very_active: 'Very active (daily)',
    local_foods: '🍽 Local foods',
    info_disclaimer:
      'These recommendations are informative only. For a precise nutrition program, consult a nutritionist or dietician.',
  },
  activite: {
    title: "🏃 Today's activity",
    intro: 'Move at least 30 min or take 7000 steps a day. +5 loyalty points per active day!',
    steps: 'Steps',
    minutes: 'Exercise minutes',
    goal_reached: '✅ Goal achieved today',
    current_streak: 'Current streak',
    best_streak: 'Best streak',
    days_active_week: 'Active days (week)',
    days_active_month: 'Active days (month)',
    save_today: '+ Save',
    bonus_credited: 'Active day recorded. +5 loyalty points added to your account.',
  },
  driver: {
    title: 'Driver space',
    earnings: 'Earnings',
    rating: 'Rating',
    deliveries_today: 'Deliveries today',
    deliveries_total: 'Total deliveries',
    active_order: 'Active order',
    no_active_order: 'No active order.',
    no_orders: 'No orders yet.',
    accept: 'Accept',
    start_delivery: 'Start delivery',
    mark_arrived: 'Arrived at patient',
    take_photo: 'Take CNI photo',
    show_badge: 'Show my QR badge',
    badge_subtitle: 'Show to the patient to confirm delivery',
    certified: 'Certified',
    not_certified: 'Not certified',
    cni_pending: 'CNI under validation',
    open_navigation: 'Open in Google Maps',
    distance_to_patient: 'Distance to patient',
    earnings_today: "Today's earnings",
    earnings_week: 'This week',
    earnings_month: 'This month',
    last_payouts: 'Recent payouts',
    no_payouts: 'No payouts yet.',
    bank_account: 'Payment account',
    update_bank: 'Update banking details',
  },
  orders: {
    title: 'My orders',
    no_orders: 'No orders yet.',
    placed_on: 'Placed on',
    items: 'items',
    total: 'Total',
    track: 'Track',
    details: 'Details',
    cancel: 'Cancel order',
    confirm_received: 'Confirm receipt',
    status_pending: 'Pending',
    status_paid: 'Paid',
    status_assigned: 'Assigned',
    status_in_transit: 'In transit',
    status_delivered: 'Delivered',
    status_cancelled: 'Cancelled',
  },
}
